import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import RateReviewIcon from '@mui/icons-material/RateReview';
import MemoryIcon from '@mui/icons-material/Memory';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from './shared/PageTitle';
import MetricCard from './shared/MetricCard';
import ThroughputChart, { ThroughputPeriod } from './dashboard/ThroughputChart';
import {
  ProjectsClient,
  ProcessingClient,
  DashboardSummaryResponse,
  ProjectListItem,
  ProcessingJobItem,
} from '../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../api/apiConfig';
import { formatStatusLabel } from './shared/StatusChip';
import { usePermissions, Permissions } from '../contexts/PermissionContext';

const stageTone = (status?: string) => {
  switch (status) {
    case 'Active':
      return { bgcolor: 'primary.50', color: 'primary.dark', borderColor: 'primary.200' };
    case 'Published':
    case 'Reported':
      return { bgcolor: 'primary.100', color: 'primary.800', borderColor: 'primary.300' };
    case 'Closed':
      return { bgcolor: 'success.50', color: 'success.dark', borderColor: 'success.200' };
    case 'Merged':
      return { bgcolor: 'warning.50', color: 'warning.dark', borderColor: 'warning.200' };
    default:
      return { bgcolor: 'grey.100', color: 'text.secondary', borderColor: 'divider' };
  }
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [jobs, setJobs] = useState<ProcessingJobItem[]>([]);
  const [throughputPeriod, setThroughputPeriod] = useState<ThroughputPeriod>('week');
  const [attentionPage, setAttentionPage] = useState(0);
  const [attentionPageSize, setAttentionPageSize] = useState(5);

  const projectsClient = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);
  const processingClient = useMemo(() => new ProcessingClient(getApiBaseUrl(), authenticatedFetch), []);

  useEffect(() => {
    if (!hasPermission(Permissions.PROJECT_READ)) {
      return;
    }

    projectsClient.getApiDashboardSummary().then(setSummary).catch(() => setSummary(null));
    let cancelled = false;
    const loadProjects = async () => {
      const loaded: ProjectListItem[] = [];
      let pageNumber = 1;
      while (!cancelled) {
        const result = await projectsClient.getApiProjects(pageNumber, 100, undefined, false);
        const items = result.items ?? [];
        loaded.push(...items);
        if (items.length === 0 || loaded.length >= (result.totalCount ?? loaded.length)) break;
        pageNumber += 1;
      }
      if (!cancelled) setProjects(loaded);
    };
    loadProjects().catch(() => { if (!cancelled) setProjects([]); });
    const loadJobs = async () => {
      const loaded: ProcessingJobItem[] = [];
      let pageNumber = 1;
      while (!cancelled) {
        const result = await processingClient.getApiProcessingJobs(pageNumber, 100, undefined);
        const items = result.items ?? [];
        loaded.push(...items);
        if (items.length === 0 || loaded.length >= (result.totalCount ?? loaded.length)) break;
        pageNumber += 1;
      }
      if (!cancelled) setJobs(loaded);
    };
    loadJobs().catch(() => { if (!cancelled) setJobs([]); });
    return () => { cancelled = true; };
  }, [hasPermission, projectsClient, processingClient]);

  const attention = useMemo(
    () =>
      [...projects]
        .sort((a, b) => (b.needsReviewCount ?? 0) - (a.needsReviewCount ?? 0)),
    [projects],
  );
  const currentAttentionPage = Math.min(attentionPage, Math.max(0, Math.ceil(attention.length / attentionPageSize) - 1));
  const visibleAttention = attention.slice(currentAttentionPage * attentionPageSize, (currentAttentionPage + 1) * attentionPageSize);

  const openConflicts = projects.filter((project) => project.conflictStatus === 'PotentialConflict').length;

  const conflictLabel = (status?: string) => {
    if (status === 'PotentialConflict') {
      return t('dashboard.potentialConflict');
    }
    if (status === 'Resolved') {
      return t('dashboard.resolved');
    }
    if (status === 'Clear') {
      return t('dashboard.clear');
    }
    return t('dashboard.unchecked');
  };

  const conflictChipSx = (status?: string) => {
    if (status === 'PotentialConflict') {
      return { bgcolor: 'warning.50', color: 'warning.dark', borderColor: 'warning.200' };
    }
    if (status === 'Clear' || status === 'Resolved') {
      return { bgcolor: 'success.50', color: 'success.dark', borderColor: 'success.200' };
    }
    return { bgcolor: 'grey.100', color: 'text.secondary', borderColor: 'divider' };
  };

  return (
    <Box>
      <PageTitle
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        actions={
          <>
            {hasPermission(Permissions.DOCUMENT_UPLOAD) && (
              <Button
                component={RouterLink}
                to="/upload"
                variant="secondary"
                size="md"
                startIcon={<FileUploadOutlinedIcon />}
              >
                {t('dashboard.uploadDocuments')}
              </Button>
            )}
            {hasPermission(Permissions.PROJECT_READ) && (
              <Button
                component={RouterLink}
                to="/projects"
                variant="primary"
                size="md"
                endIcon={<ArrowForwardIcon />}
              >
                {t('dashboard.allProjects')}
              </Button>
            )}
          </>
        }
      />

      {hasPermission(Permissions.PROJECT_READ) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              gap: '16px',
            }}
          >
            <MetricCard
              label={t('dashboard.activeProjects')}
              value={summary?.projectCount ?? 0}
              icon={<FolderIcon />}
              to="/projects"
            />
            <MetricCard
              label={t('dashboard.fieldsNeedingReview')}
              value={summary?.needsReviewCount ?? 0}
              icon={<RateReviewIcon />}
              tone="warning"
              to="/projects"
              hint={t('dashboard.fieldsNeedingReviewHint')}
            />
            <MetricCard
              label={t('dashboard.openConflicts')}
              value={openConflicts}
              icon={<GppMaybeIcon />}
              tone="error"
              to="/conflicts"
            />
            <MetricCard
              label={t('dashboard.documentsProcessing')}
              value={summary?.inProgressJobCount ?? 0}
              icon={<MemoryIcon />}
              to="/processing"
            />
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                px: '24px',
                pt: '20px',
                pb: '16px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '16px',
              }}
            >
              <Box>
                <Typography variant="textMD" sx={{ fontSize: 16, fontWeight: 600 }}>{t('dashboard.attentionTitle')}</Typography>
                <Typography variant="textSM" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('dashboard.attentionSubtitle')}
                </Typography>
              </Box>
              <Typography
                component={RouterLink}
                to="/projects"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('dashboard.viewAll')}
              </Typography>
            </Box>
            {attention.length === 0 ? (
              <Typography color="text.secondary" sx={{ px: '24px', pb: '24px' }}>
                {t('dashboard.noProjects')}
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto', px: '24px', pb: '24px' }}>
                <Table
                  size="small"
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    borderCollapse: 'separate',
                    overflow: 'hidden',
                    tableLayout: 'fixed',
                    minWidth: 1000,
                    '& th, & td': { fontSize: 13, py: 1, px: 1.5 },
                    '& th': { fontWeight: 600 },
                    '& th:nth-of-type(1)': { width: '24%' },
                    '& th:nth-of-type(2)': { width: '24%' },
                    '& th:nth-of-type(3)': { width: '20%' },
                    '& th:nth-of-type(4)': { width: '10%' },
                    '& th:nth-of-type(5)': { width: '16%' },
                    '& th:nth-of-type(6)': { width: '6%' },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('dashboard.reference')}</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('dashboard.cargoVessel')}</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('dashboard.client')}</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('dashboard.stage')}</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.50' }}>{t('dashboard.conflicts')}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'grey.50' }}>{t('dashboard.docs')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAttention.map((project) => (
                      <TableRow
                        key={project.id}
                        hover
                        onClick={() => project.id && navigate(`/projects/${project.id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Typography
                              noWrap
                              title={project.internalReference}
                              sx={{ fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace', fontSize: 13, fontWeight: 600, minWidth: 0 }}
                            >
                              {project.internalReference}
                            </Typography>
                            {!!project.needsReviewCount && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.25,
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  bgcolor: 'warning.50',
                                  color: 'warning.dark',
                                  flexShrink: 0,
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                <WarningAmberIcon sx={{ fontSize: 12 }} />
                                {project.needsReviewCount}
                              </Box>
                            )}
                          </Box>
                          {project.externalReference && (
                            <Typography noWrap title={project.externalReference} color="text.secondary" sx={{ fontSize: 12, display: 'block', mt: 0.25 }}>
                              {project.externalReference}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography noWrap title={project.cargoDescription} sx={{ fontSize: 13, fontWeight: 600 }}>
                            {project.cargoDescription || '-'}
                          </Typography>
                          <Typography noWrap title={project.vesselName} color="text.secondary" sx={{ fontSize: 12 }}>
                            {project.vesselName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography noWrap title={project.externalClientName} color="text.secondary" sx={{ fontSize: 13 }}>
                            {project.externalClientName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatStatusLabel(project.status || '-')}
                            variant="outlined"
                            sx={{
                              height: 24,
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 1,
                              ...stageTone(project.status),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={
                              project.conflictStatus === 'PotentialConflict' ? (
                                <WarningAmberIcon />
                              ) : (
                                <CheckCircleOutlineIcon />
                              )
                            }
                            label={conflictLabel(project.conflictStatus)}
                            variant="outlined"
                            sx={{
                              height: 24,
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 1,
                              '& .MuiChip-icon': { fontSize: 14 },
                              ...conflictChipSx(project.conflictStatus),
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'text.secondary',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                            {project.documentCount ?? 0}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={attention.length}
                  page={currentAttentionPage}
                  rowsPerPage={attentionPageSize}
                  rowsPerPageOptions={[5, 10, 15]}
                  onPageChange={(_, page) => setAttentionPage(page)}
                  onRowsPerPageChange={(event) => { setAttentionPageSize(Number(event.target.value)); setAttentionPage(0); }}
                  labelRowsPerPage={t('common.pageSize')}
                  labelDisplayedRows={({ from, to, count }) => t('common.paginationRange', { from, to, count })}
                  getItemAriaLabel={(type) => t(`common.${type}Page`)}
                />
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 2, px: '24px', pt: '20px', pb: '24px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{t('dashboard.throughputTitle')}</Typography>
                <Typography variant="textSM" color="text.secondary" sx={{ mt: '6px', display: 'block' }}>
                  {t('dashboard.throughputSubtitle')}
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={throughputPeriod}
                exclusive
                size="small"
                aria-label={t('dashboard.throughputPeriod')}
                onChange={(_, value: ThroughputPeriod | null) => value && setThroughputPeriod(value)}
                sx={{ ml: 'auto' }}
              >
                {(['day', 'week', 'month'] as const).map((period) => (
                  <ToggleButton key={period} value={period}>{t(`dashboard.${period}`)}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <ThroughputChart jobs={jobs} period={throughputPeriod} />
          </Paper>
        </Box>
      ) : (
        <Typography variant="textMD" color="text.secondary">
          {t('common.comingSoon')}
        </Typography>
      )}
    </Box>
  );
};

export default Dashboard;
